import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PersonNameFields from '@/components/PersonNameFields';
import { Loader2, Save } from 'lucide-react';
import { ProfileUpdateData, PersonName } from '@/contexts/auth/types';

interface OrganizerProfileFormProps {
  firstName: string;
  lastName: string;
  academicTitle: string;
  formalSalutation: string;
  contactPhone: string;
  isLoading: boolean;
  onSubmit: (data: ProfileUpdateData) => Promise<void>;
  errors?: {
    contactPhone?: string;
    formalSalutation?: string;
  }
}

const OrganizerProfileForm: React.FC<OrganizerProfileFormProps> = ({
  firstName,
  lastName,
  academicTitle,
  formalSalutation,
  contactPhone,
  isLoading,
  onSubmit,
  errors = {}
}) => {
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    academicTitle: '',
    formalSalutation: '',
    contactPhone: '',
  });

  useEffect(() => {
    setProfileData({
      firstName,
      lastName,
      academicTitle,
      formalSalutation,
      contactPhone,
    });
  }, [firstName, lastName, academicTitle, formalSalutation, contactPhone]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Create name object from form fields
    const name: PersonName = {
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      academicTitle: profileData.academicTitle || undefined,
      formalSalutation: profileData.formalSalutation || undefined
    };

    // Combine profile data
    const updatedProfileData: ProfileUpdateData = {
      owner: `/me`,
      name,
      contactPhone: profileData.contactPhone || undefined,
    };

    await onSubmit(updatedProfileData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profil szerkesztése</CardTitle>
        <CardDescription>Frissítse személyes és kapcsolattartási adatait</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <PersonNameFields
            firstName={profileData.firstName}
            setFirstName={(value) => setProfileData(prev => ({ ...prev, firstName: value }))}
            lastName={profileData.lastName}
            setLastName={(value) => setProfileData(prev => ({ ...prev, lastName: value }))}
            academicTitle={profileData.academicTitle}
            setAcademicTitle={(value) => setProfileData(prev => ({ ...prev, academicTitle: value }))}
          />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">


            <div className="space-y-2">
              <Label htmlFor="contactPhone">Telefonszám</Label>
              <Input
                id="contactPhone"
                name="contactPhone"
                value={profileData.contactPhone}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="formalSalutation">Megszólítás</Label>
              <Input
                id="formalSalutation"
                name="formalSalutation"
                value={profileData.formalSalutation}
                onChange={(e) => setProfileData(prev => ({ ...prev, formalSalutation: e.target.value }))}
                placeholder="pl. Dr. X.Y."
                className={errors.formalSalutation ? "border-red-500" : ""}
              />
              {errors.formalSalutation && (
                <p className="text-sm text-red-500 mt-1">{errors.formalSalutation}</p>
              )}
            </div>


          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            className="w-full md:w-auto"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Mentés...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Profil mentése
              </>
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default OrganizerProfileForm;
